import { Metric, GoalStatus, ModelType } from '../types';

// ============ Metric Formatting ============

/**
 * Format a value based on its metric type.
 * Example: formatMetricValue(20000, 'sales') => '€20,000'
 */
export const formatMetricValue = (value: number, metric: Metric): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  });

  switch (metric) {
    case 'sales':
    case 'cost':
    case 'spend':
    case 'cpa':
      return `€${formatter.format(value)}`;
    case 'roi':
    case 'roas':
      return `${formatter.format(value)}%`;
    default:
      return formatter.format(value);
  }
};

/**
 * Get the unit suffix for a metric.
 * Example: getMetricUnit('visits') => 'visits'
 */
export const getMetricUnit = (metric: Metric): string => {
  switch (metric) {
    case 'sales':
    case 'cost':
    case 'spend':
    case 'cpa':
      return '€';
    case 'roi':
    case 'roas':
      return '%';
    case 'visits':
      return 'visits';
    case 'conversions':
      return 'conversions';
    case 'units_sold':
      return 'units';
    default:
      return '';
  }
};

/**
 * Format target value with metric-appropriate display.
 * Example: formatTargetDisplay(20000, 'sales') => '€20,000'
 * Example: formatTargetDisplay(200, 'roi') => '200% ROI'
 */
export const formatTargetDisplay = (value: number, metric: Metric): string => {
  const formatted = formatMetricValue(value, metric);

  switch (metric) {
    case 'roi':
      return `${formatted} ROI`;
    case 'roas':
      return `${formatted} ROAS`;
    case 'visits':
      return `${formatted} visits`;
    case 'conversions':
      return `${formatted} conversions`;
    case 'units_sold':
      return `${formatted} units`;
    default:
      return formatted;
  }
};

// ============ Status Formatting ============

export const STATUS_DISPLAY: Record<GoalStatus, string> = {
  on_track: 'On Track',
  completed: 'Completed',
  behind: 'Behind',
};

export const STATUS_COLORS: Record<GoalStatus, { bg: string; color: string }> =
  {
    on_track: { bg: 'green.50', color: 'green.600' },
    completed: { bg: 'gray.100', color: 'gray.600' },
    behind: { bg: 'red.50', color: 'red.600' },
  };

// ============ Model Type Formatting ============

export const MODEL_TYPE_DISPLAY: Record<ModelType, string> = {
  da: 'Digital Attribution',
  mm: 'Marketing Mix Modelling',
  mo: 'Marketing Optimization',
  lt: 'Long Term Effects',
};

export const MODEL_TYPE_PATHS: Record<ModelType, string> = {
  da: '/digital/overview',
  mm: '/mmm/overview',
  mo: '/marketing-optimization',
  lt: '/longterm-effect',
};

// ============ Metric Options ============

export const METRIC_OPTIONS: { value: Metric; label: string }[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'conversions', label: 'Conversions' },
  { value: 'visits', label: 'Visits' },
  { value: 'units_sold', label: 'Units Sold' },
  { value: 'cost', label: 'Cost' },
  { value: 'spend', label: 'Spend' },
  { value: 'roi', label: 'ROI' },
  { value: 'roas', label: 'ROAS' },
  { value: 'cpa', label: 'CPA' },
];

export const MODEL_TYPE_OPTIONS: { value: ModelType; label: string }[] = [
  { value: 'da', label: 'Digital Attribution' },
  { value: 'mm', label: 'Marketing Mix Modelling' },
  { value: 'mo', label: 'Marketing Optimization' },
  { value: 'lt', label: 'Long Term Effects' },
];

// ============ Metric Label Formatting ============

/**
 * Get display label for a metric.
 * Example: formatMetricLabel('units_sold') => 'Units Sold'
 */
export const formatMetricLabel = (metric: Metric): string => {
  const option = METRIC_OPTIONS.find((opt) => opt.value === metric);
  return option?.label ?? metric;
};

// ============ Date Formatting ============

/**
 * Format ISO date string to display format.
 * Example: formatGoalDate('2025-08-28') => 'Aug 28, 2025'
 */
export const formatGoalDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
